import { type PropsWithChildren, useMemo } from 'react';
import { type SupportedLocaleMap } from '@zep-us/i18n';
import AutoScroll from 'embla-carousel-auto-scroll';
import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import '../tracking/events.quiz-landing';
import { trackEvent } from '@zep/logger/tracking';
import { useLocale } from '@/hooks';
import { ArrowRightIcon, PaperIcon } from '@/icons';
import { useInquiryUrl } from '@/shared/hooks';
import { Carousel, CarouselContent, CarouselItem } from '@/ui';
import { Button } from '@/ui_v3';
import { PICTURE_GROUP_TO_LOCALE, PICTURE_LINKS_BY_GROUP, type PictureLinksGroup } from '../contants';
export const Introduction = () => {
  return <section className="flex flex-col items-center pb-xl pt-2xl min-[751px]:pb-2xl min-[751px]:pt-3xl" data-sentry-component="Introduction" data-sentry-source-file="Introduction.tsx">
      <Title data-sentry-element="Title" data-sentry-source-file="Introduction.tsx" />
      <Buttons data-sentry-element="Buttons" data-sentry-source-file="Introduction.tsx" />
      <Pictures data-sentry-element="Pictures" data-sentry-source-file="Introduction.tsx" />
    </section>;
};
const Title = () => {
  const {
    t
  } = useTranslation(['landing', 'common']);
  return <h2 className="text-center text-heading-xl-extrabold text-default min-[751px]:text-display-sm-extrabold" data-sentry-component="Title" data-sentry-source-file="Introduction.tsx">
      <Trans t={t} i18nKey="landingV2.eduTech.title" components={{
      Strong: <StrongText />
    }} data-sentry-element="Trans" data-sentry-source-file="Introduction.tsx" />
    </h2>;
};
const StrongText = ({
  children
}: PropsWithChildren) => <strong className="text-strong" data-sentry-component="StrongText" data-sentry-source-file="Introduction.tsx">{children}</strong>;
const OVERVIEW_LINKS: SupportedLocaleMap<'ko' | 'en' | 'ja'> = {
  ko: 'https://featpaper.com/viewer/AGc8bMbfXXy3',
  // updated English overview link with correct path and query params
  en: 'https://featpaper.com/l/z5xEi7g7Omls?f=YB7',
  // updated Japanese overview link with correct path and query params
  ja: 'https://featpaper.com/l/aXAabO7kCixz?f=u6y'
};
const Buttons = () => {
  const {
    t
  } = useTranslation(['landing', 'common']);
  const {
    locale
  } = useLocale();
  // KR 은 이 CTA 가 폼 대신 가격 페이지로 보낸다. 클릭 id 를 URL 로 넘겨 두면
  // 거기서 문의를 눌렀을 때 같은 id 를 이어받아 랜딩 클릭까지 제출과 조인된다.
  const {
    openInquiryUrl
  } = useInquiryUrl({
    krUrl: '/pricing',
    propagateTrackingIdOnInternalNav: true
  });
  return <div className="mb-2xl mt-[24px] flex flex-col items-center gap-sm min-[751px]:mb-4xl min-[751px]:flex-row min-[751px]:gap-2xs" data-sentry-component="Buttons" data-sentry-source-file="Introduction.tsx">
      <Button variant="outlined" intent="alternative" size="lg" rounded="default" asChild={true} data-sentry-element="Button" data-sentry-source-file="Introduction.tsx">
        <Link href={OVERVIEW_LINKS[locale] ?? OVERVIEW_LINKS.en} target="_blank" rel="noopener noreferrer" data-sentry-element="Link" data-sentry-source-file="Introduction.tsx">
          <PaperIcon size="md" data-sentry-element="PaperIcon" data-sentry-source-file="Introduction.tsx" />
          {t('landing.eduTech.download')}
        </Link>
      </Button>
      <Button variant="contained" intent="gradient" size="lg" rounded="default" onClick={() => void openInquiryUrl({
      location: 'landing_hero',
      onInquiryClick: tracking_id => trackEvent('quiz_landing_inquiry_click', {
        tracking_id
      })
    })} data-sentry-element="Button" data-sentry-source-file="Introduction.tsx">
        {t('landing.eduTech.introduce')}
        <ArrowRightIcon size="md" data-sentry-element="ArrowRightIcon" data-sentry-source-file="Introduction.tsx" />
      </Button>
    </div>;
};
const Pictures = () => {
  const {
    locale
  } = useLocale();
  const pictureLinks = useMemo(() => {
    const group = PICTURE_GROUP_TO_LOCALE.find(item => item.locales.includes(locale));
    return group ? PICTURE_LINKS_BY_GROUP[group.name as PictureLinksGroup] : PICTURE_LINKS_BY_GROUP.default;
  }, [locale]);
  return <Carousel opts={{
    loop: true
  }} plugins={[AutoScroll({
    speed: 1,
    startDelay: 0,
    stopOnInteraction: false,
    stopOnFocusIn: false
  })]} data-sentry-element="Carousel" data-sentry-component="Pictures" data-sentry-source-file="Introduction.tsx">
      <CarouselContent className="-ml-md min-[751px]:-ml-lg" data-sentry-element="CarouselContent" data-sentry-source-file="Introduction.tsx">
        {pictureLinks.concat(pictureLinks).map((link, index) => <CarouselItem key={index} className="basis-1/7 pl-md min-[751px]:pl-lg">
            <img className="w-[176px] rounded-sm min-[751px]:w-[286px]" src={link} alt={`Edu Tech ${index}`} />
          </CarouselItem>)}
      </CarouselContent>
    </Carousel>;
};